import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition, SlotGroupResult } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  usePage,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Parent pages list, shallow-merged with this theme's `src/pages.json`
// overrides (Future Residents Club, Contact position). The runtime doesn't
// expose page metadata (position / parent / template) through a hook, and
// parent-src imports are lint errors. Belmont's package.json doesn't list
// `./pages.json` in its exports map, but the platform's Vite alias resolves
// the bare specifier at build time.
// @ts-ignore — @vibesites/theme-belmont/pages.json is resolved at build time
//              via the Vite alias.
import basePagesData from '@vibesites/theme-belmont/pages.json';
import localPagesData from '../pages.json';

// ============================================================================
// Dynamic Navigation from pages.json
// ----------------------------------------------------------------------------
// Nav-page filter matches the Weidner Blue Haven header groupings so this
// company theme surfaces the same pages and dropdown structure. Surrounding
// header chrome (utility row, Schedule a Tour CTA, drawer) stays Belmont.
//
//   • `home` is an explicit nav link.
//   • `contact` is the last nav link (parent promotes Apply Now / Resident
//     Access to the utility row; Schedule a Tour stays the header CTA).
//   • `apply-now` and `resident-access` are excluded from the row.
//   • Three synthetic dropdowns group related pages under a single label:
//       - Find Your Home  → Floor Plans + Interactive Map
//       - Features        → Amenities + Pet Policy + Future Residents Club
//       - Gallery & Tours → Gallery + Virtual Tour + Schedule a Tour
//     Groups collapse to their single surviving child when the property
//     doesn't have the others (e.g. no interactive map).
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

export interface NavLink {
  alias?: string;
  label: string;
  href?: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`, or
      when a synthetic group defines child aliases explicitly. */
  children?: NavLink[];
}

interface NavGroup {
  label: string;
  aliases: string[];
}

const NAV_EXCLUDED_ALIASES = new Set<string>([
  'privacy-policy',
  'sitemap',
  'accessibility',
  // Apply Now + Resident Access are utility-row / drawer CTAs.
  'apply-now',
  'thank-you',
  'resident-access',
  // `schedule-a-tour` participates in the "Gallery & Tours" dropdown.
  'hyperlocal',
  'reviews',
]);

const SYNTHETIC_NAV_GROUPS: NavGroup[] = [
  { label: 'Find Your Home', aliases: ['floor-plans', 'interactive-map'] },
  { label: 'Features', aliases: ['amenities', 'pet-policy', 'future-residents-club'] },
  { label: 'Gallery & Tours', aliases: ['gallery', 'virtual-tour', 'schedule-a-tour'] },
];

const SYNTHETIC_GROUP_ALIASES = new Set(SYNTHETIC_NAV_GROUPS.flatMap((group) => group.aliases));

/** Shallow-merge parent pages with this theme's pages.json overrides so nav
    sees local additions (Future Residents Club) and field tweaks (Contact
    position) the same way the platform page manifest does. */
function mergePagesData(
  base: Record<string, PageEntry>,
  local: Record<string, Partial<PageEntry>>,
): Record<string, PageEntry> {
  const merged: Record<string, PageEntry> = { ...base };
  for (const [alias, override] of Object.entries(local)) {
    merged[alias] = { ...(merged[alias] ?? {}), ...override } as PageEntry;
  }
  return merged;
}

const pagesData = mergePagesData(
  basePagesData as Record<string, PageEntry>,
  localPagesData as Record<string, Partial<PageEntry>>,
);

/** Normalize a path for comparison: drop the query/hash and any trailing
    slash so "/floor-plans/", "/floor-plans?x=1" and "/floor-plans" all match,
    while keeping the root "/" intact. */
function normalizePath(p?: string | null): string {
  if (!p) return '/';
  const path = p.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path || '/';
}

/** A nav link is "active" when its href matches the current page — or, for a
    parent / synthetic group with a dropdown, when any of its children match
    (so the parent stays highlighted while you're on one of its sub-pages). */
function isNavLinkActive(link: NavLink, currentPath: string): boolean {
  const current = normalizePath(currentPath);
  if (link.href && normalizePath(link.href) === current) return true;
  return !!link.children?.some((c) => !!c.href && normalizePath(c.href) === current);
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 *
 * Synthetic groups are ANCHORED to the FIRST alias in `aliases` — that
 * alias's page position places the group in the nav row.
 *
 * `hasInteractiveLeasing` gates the Interactive Map page — properties that
 * don't have the ApartmentSync (or override) config don't get the map page
 * in the nav, and the synthetic Find Your Home dropdown collapses to plain
 * Floor Plans.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];
  const consumedAliases = new Set<string>();

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { alias, label: page.name, href: page.path });
  }

  // Second pass: attach any pages that still use a real pages.json `parent`.
  // Synthetic groups below own their aliases explicitly so they don't inherit
  // parent relationships like `interactive-map -> floor-plans`.
  for (const [alias, page] of eligible) {
    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;
    if (page.parent && SYNTHETIC_GROUP_ALIASES.has(page.parent)) continue;
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    }
  }

  const emittedGroups = new Set<NavGroup>();
  const emitSyntheticGroup = (group: NavGroup) => {
    if (emittedGroups.has(group)) return;
    emittedGroups.add(group);
    const children = group.aliases
      .map((childAlias) => linksByAlias.get(childAlias))
      .filter((link): link is NavLink => !!link);
    for (const child of children) {
      if (child.alias) consumedAliases.add(child.alias);
    }
    if (children.length === 1) {
      topLevel.push(children[0]);
    } else if (children.length > 1) {
      topLevel.push({ label: group.label, children });
    }
  };

  for (const [alias, page] of eligible) {
    if (consumedAliases.has(alias)) continue;

    const anchoredGroup = SYNTHETIC_NAV_GROUPS.find((item) => item.aliases[0] === alias);
    if (anchoredGroup) {
      emitSyntheticGroup(anchoredGroup);
      continue;
    }

    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;

    const parent = page.parent;
    if (parent && eligibleAliases.has(parent) && parent !== alias) continue;

    topLevel.push(linksByAlias.get(alias)!);
  }

  for (const group of SYNTHETIC_NAV_GROUPS) {
    emitSyntheticGroup(group);
  }

  return topLevel.filter((link) => !link.children || link.children.length > 0);
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Primary header CTA. Labeled "Schedule a Tour" (rendered as a solid, filled
// button — see `.nav-cta-btn` below). The slot path stays
// `header.schedule_tour_link` so existing saved content / bindings keep
// resolving; only the displayed label + default destination changed.
const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address in the desktop utility row (next to the phone) and the mobile
// drawer. Promoted from a bare `header.full_address` content token +
// auto-generated Google Maps directions URL to a slot group so editors
// can pick the address text, the destination URL, and the link target
// independently — rather than having the URL forced to the property's
// `google_place_url` (or a synthesized `/maps/dir/?destination=...`
// fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// View Floor Plans CTA inside the mobile drawer. Hard-coded `/floor-plans`
// before; promoting to a slot group so the editor can retarget the
// button (e.g. to an off-site availability widget) and relabel it. Mirrors
// the schedule-tour link shape so the editor experience is consistent.
const viewFloorPlansLinkDef: SlotGroupDefinition = {
  label: 'View Floor Plans Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Hover-menu state
// ----------------------------------------------------------------------------
// Dropdowns sit below the trigger. Instant mouseleave across the old
// 4px mt-1 gap closed the panel before the pointer reached the
// sublinks — especially on Features and Gallery & Tours. A short close
// delay plus a padding-top hover bridge (not a margin gap) lets the
// pointer travel into the panel without collapsing the menu.
// ============================================================================

const DROPDOWN_CLOSE_DELAY_MS = 180;

function useHoverMenu(setOpen: React.Dispatch<React.SetStateAction<boolean>>) {
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer, setOpen]);

  const closeMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer, setOpen]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), DROPDOWN_CLOSE_DELAY_MS);
  }, [clearCloseTimer, setOpen]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return { openMenu, closeMenu, scheduleClose };
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link, currentPath }: { link: NavLink; currentPath: string }) {
  const hasChildren = !!(link.children && link.children.length);
  const active = isNavLinkActive(link, currentPath);
  const [open, setOpen] = useState(false);
  const { openMenu, closeMenu, scheduleClose } = useHoverMenu(setOpen);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') closeMenu();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, closeMenu]);

  const closeIfFocusLeft = (e: React.FocusEvent<HTMLElement>) => {
    if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
      closeMenu();
    }
  };

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        aria-current={active ? 'page' : undefined}
        className={`rise-nav-darken-hover font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors py-2 whitespace-nowrap${active ? ' nav-link-active' : ''}`}
      >
        {link.label}
      </a>
    );
  }

  const triggerClass = `rise-nav-darken-hover font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap${active ? ' nav-link-active' : ''}`;

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      onBlur={closeIfFocusLeft}
    >
      {/* Synthetic group labels don't have their own page (no href), so they
          render as a chevron-only trigger button. Real parent pages keep the
          <a> so clicking the label navigates to that page. */}
      {link.href ? (
        <a
          href={link.href}
          aria-current={active ? 'page' : undefined}
          className={triggerClass}
          aria-haspopup="true"
          aria-expanded={open}
          onFocus={openMenu}
        >
          {link.label}
          <ChevronDownIcon
            className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </a>
      ) : (
        <button
          type="button"
          onClick={() => (open ? closeMenu() : openMenu())}
          onFocus={openMenu}
          className={`${triggerClass} bg-transparent`}
          aria-haspopup="true"
          aria-expanded={open}
        >
          {link.label}
          <ChevronDownIcon
            className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      )}

      <div
        className={`nav-more-dropdown absolute left-0 top-full z-[70] pt-1 min-w-[200px] transition-[opacity,transform] duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0 pointer-events-auto'
            : 'opacity-0 invisible -translate-y-1 pointer-events-none'
        }`}
      >
        {/* pt-1 above is a hover bridge (not a margin gap) so the pointer
            can travel from the trigger into the panel without leaving the
            menu's hit area. The bordered surface is the inner box. */}
        <div
          className="border border-[var(--vibe-color-border)] shadow-lg"
          style={{ backgroundColor: 'var(--vibe-color-background)' }}
        >
          {link.children!.map((child) => {
            const childActive = !!child.href && normalizePath(child.href) === normalizePath(currentPath);
            return (
              <a
                key={child.href || child.label}
                href={child.href}
                aria-current={childActive ? 'page' : undefined}
                className={`nav-dropdown-link rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors${childActive ? ' nav-link-active' : ''}`}
              >
                {child.label}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks, currentPath }: { navLinks: NavLink[]; currentPath: string }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const { openMenu: openMore, closeMenu: closeMore, scheduleClose: scheduleCloseMore } =
    useHoverMenu(setMoreOpen);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        closeMore();
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen, closeMore]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link, i) => (
          <span
            key={link.href || link.label}
            className="font-body text-[13px] font-normal tracking-wider inline-flex items-center"
          >
            {/* Mirror the visible separator so each measured item includes the
                dot + its margin — keeps the overflow math accurate. */}
            {i > 0 ? <span className="nav-sep" aria-hidden="true">·</span> : null}
            <span className="inline-flex items-center gap-1">
              {link.label}
              {link.children && link.children.length ? (
                <ChevronDownIcon className="w-3.5 h-3.5" />
              ) : null}
            </span>
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-center gap-2 xl:gap-3 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link, i) => (
          <span
            key={link.href || link.label}
            className="inline-flex items-center font-body text-[13px] font-normal tracking-wider"
          >
            {/* Dot separator between page names (matches the demo). Sits
                between the links — never before the first one — and is a
                sibling of the link so it stays out of the active-link bar. */}
            {i > 0 ? <span className="nav-sep" aria-hidden="true">·</span> : null}
            <DesktopNavLink link={link} currentPath={currentPath} />
          </span>
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // "Floor Plans" parent dropdown so the two affordances feel
            // identical. The click handler stays as a fallback for
            // touch devices where hover doesn't fire, and the
            // click-outside listener still dismisses the menu when the
            // pointer leaves the group entirely.
            onMouseEnter={openMore}
            onMouseLeave={scheduleCloseMore}
            onBlur={(e) => {
              if (!moreRef.current?.contains(e.relatedTarget as Node)) {
                closeMore();
              }
            }}
          >
            <button
              onClick={() => (moreOpen ? closeMore() : openMore())}
              onFocus={openMore}
              className="rise-nav-darken-hover font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full z-[70] pt-1 min-w-[200px] transition-[opacity,transform] duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0 pointer-events-auto'
                  : 'opacity-0 invisible -translate-y-1 pointer-events-none'
              }`}
            >
              <div
                className="border border-[var(--vibe-color-border)] shadow-lg"
                style={{ backgroundColor: 'var(--vibe-color-background)' }}
              >
                {overflowLinks.map((link) => (
                  <React.Fragment key={link.href || link.label}>
                    {link.href ? (
                      <a
                        href={link.href}
                        aria-current={normalizePath(link.href) === normalizePath(currentPath) ? 'page' : undefined}
                        className={`nav-dropdown-link rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors${normalizePath(link.href) === normalizePath(currentPath) ? ' nav-link-active' : ''}`}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <div className="block px-5 py-3 font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text)]">
                        {link.label}
                      </div>
                    )}
                    {link.children?.map((child) => (
                      <a
                        key={child.href || child.label}
                        href={child.href}
                        aria-current={!!child.href && normalizePath(child.href) === normalizePath(currentPath) ? 'page' : undefined}
                        // Indent child rows so the More dropdown reads as a
                        // nested list when a parent gets pushed into overflow
                        // and we want its sub-items to remain reachable.
                        className={`nav-dropdown-link rise-nav-darken-hover block pl-9 pr-5 py-3 font-body text-[13px] font-normal tracking-wider text-[var(--vibe-color-text-muted)] transition-colors${!!child.href && normalizePath(child.href) === normalizePath(currentPath) ? ' nav-link-active' : ''}`}
                      >
                        {child.label}
                      </a>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  currentPath,
  isChild,
}: {
  link: NavLink;
  onNavigate: () => void;
  currentPath: string;
  /** Child pages nest under a synthetic group (Features, Gallery & Tours,
      Find Your Home). Used to indent those rows so the drawer hierarchy
      matches the heading treatment on the parent label. */
  isChild?: boolean;
}) {
  const rowClass =
    'block py-3.5 px-6 font-body text-sm font-normal tracking-wider text-[var(--vibe-color-text)] transition-colors border-b border-[var(--vibe-color-border)]/50';

  // Synthetic group labels have no page of their own. Style them as
  // section headings — not lookalike link rows — so Features / Gallery
  // & Tours / Find Your Home read as grouping labels, not dead taps.
  if (!link.href) {
    return (
      <div role="heading" aria-level={2} className="mobile-nav-group-label">
        {link.label}
      </div>
    );
  }

  const active = normalizePath(link.href) === normalizePath(currentPath);
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={`rise-nav-darken-hover ${rowClass}${isChild ? ' mobile-nav-child' : ''}${active ? ' nav-link-active' : ''}`}
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Utility row (desktop) + main nav bar.
// Mobile main bar: hamburger | centered logo | compact Schedule CTA.
// Desktop main bar: logo | nav links | Schedule CTA.
// Utility links (phone, Apply Now, Resident Access) live in the desktop
// utility row and fold into the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  // The header is a solid, opaque white bar that does NOT overlay the hero
  // (matching the Belmont demo) — nav text / logo / CTA all render against the
  // white background, so there's no transparent-over-hero state, scroll-driven
  // color transition, or luminance-based contrast picking to manage anymore.
  // A spacer below reserves the header's height in the document flow so the
  // hero (and every page's content) starts beneath the bar.

  // Current page path — drives the active-link "double bar" indicator. We
  // read it from the runtime page context (available at SSG time AND on the
  // client) so the active state is baked into the static HTML and matches on
  // hydration; the window.location fallback only kicks in for preview/dev
  // paths that aren't wired through usePage().
  const pageCtx = usePage();
  const currentPath =
    pageCtx?.path ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive
  // leasing view" widget on that page. We require BOTH the JavaScript
  // ID (script URL token) and the Config ID (site identifier) — a
  // property with only one populated is a half-configured state and
  // shouldn't show the page (e.g. Centre Point, which has neither, was
  // leaking into the nav when only the JavaScript ID was checked).
  // Mirrors the SSG-side hide + noindex logic in ssg-entry.ts so
  // navigation, sitemap, and crawler hints stay consistent. The fields
  // are custom property fields (typed as `text`) so we read them via
  // `useProperty()` directly rather than the strongly-typed
  // `usePropertyField` (which only knows the built-in scalar fields).
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;

  // Editor-configurable override. The `header.interactive_map_show_if_exists`
  // token can be bound to a property field (Field mode — e.g.
  // `custom_engrain_sightmap_url`) or given a literal string (Custom mode).
  // In both cases useContentToken returns the already-RESOLVED value (the
  // field's value, or the literal), so we simply expose the Interactive Map
  // page whenever that resolved value is non-empty. Blank by default (no
  // override). ADDITIVE to the ApartmentSync detection above, so existing
  // ApartmentSync-configured properties keep working untouched.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Content tokens
  const phone = useContentToken('header.phone');

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  // The header bar is always solid white, so the logo always needs to read
  // against white: prefer the property's mono-black logo, falling back to
  // whatever the editor picked (typically the full-color primary logo).
  const logoImage = monoBlackLogo || logoRaw.image;
  const logo: SlotGroupResult = { ...logoRaw, image: logoImage };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const viewFloorPlansLink = useSlotGroup('header.view_floor_plans_link', viewFloorPlansLinkDef);

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  // Measure header height for spacer
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      <header
        ref={headerRef}
        className="header-scrolled fixed left-0 right-0 top-0 z-50 w-full"
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {/* Editor-only control. Holds the name of the property field whose
            presence exposes the Interactive Map page in the nav (consumed by
            `hasInteractiveMapOverride` above — e.g. `custom_engrain_sightmap_url`).
            Rendered visually hidden because it's a configuration value, not
            display copy, but wrapped in EditableToken so it's reachable from
            the editor sidebar / inline editor and satisfies the schema linter. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — desktop only. Sits between the SpecialOfferBanner
              (which adds its height to the header-bar's paddingTop above)
              and the main nav row below. Hosts the phone, address, Apply Now, and
              Resident Access links that previously lived inline in the main
              nav's right group. The links remain available on mobile via
              the drawer further down — we don't surface a second row on
              small screens because vertical space matters more there.
              `header-util-row` paints a thin bottom border so the row
              reads as visually separate from the main nav; the border
              color follows the transparent/scrolled state via the
              overrides in the <style> block. */}
          {/* ============================================================ */}
          <div className="header-util-row hidden lg:block">
            <div className="px-4 sm:px-6 lg:px-8">
              {/* Two-group layout: phone + address sit on the left edge,
                  Apply Now + Resident Access stay on the right. `justify-
                  between` pushes the groups to opposite edges; each group is
                  its own flex row so the inner dividers + gaps behave the
                  same as before. */}
              <div className="flex items-center justify-between gap-1 h-9">
                {/* Left-aligned group: phone + address. The address reuses
                    the same `header.address_link` slot group as the mobile
                    drawer — label is `property_full_address`, href is the
                    editor URL or the mobile-safe Google Maps fallback. */}
                <div className="flex items-center gap-1 min-w-0">
                  {/* Phone */}
                  <a
                    href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                    className="dniphone dniphonehref header-util-link"
                    aria-label="Call us"
                  >
                    <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <EditableToken
                      path="header.phone"
                      defaultField="property_call_tracking_phone"
                      defaultValue="(555) 555-5555"
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>

                  <span className="header-util-divider" aria-hidden="true" />

                  {/* Address → Google Maps */}
                  <EditableSlotGroup
                    groupPath="header.address_link"
                    definition={addressLinkDef}
                    className="min-w-0"
                  >
                    <a
                      href={addressLink.url || propertyAddressUrl || '#'}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="header-util-link header-util-address"
                      aria-label="Open address in Google Maps"
                    >
                      <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{addressLink.address}</span>
                    </a>
                  </EditableSlotGroup>
                </div>

                {/* Right-aligned group: utility links. flex-shrink-0 so a
                    long address ellipsizes on the left instead of colliding
                    with Apply Now / Resident Access. */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Apply Now */}
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      className="header-util-link"
                      style={{ textTransform: 'none', fontSize: '13px' }}
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>

                  <span className="header-util-divider" aria-hidden="true" />

                  {/* Resident Access */}
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      className="header-util-link"
                      style={{ textTransform: 'none', fontSize: '13px' }}
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
          </div>

          <div className="px-3 sm:px-6 lg:px-8">
            {/* Mobile: equal side columns keep the logo optically centered
                (hamburger left / one-line Schedule CTA right). Desktop (lg+):
                logo | nav links | Schedule CTA. */}
            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:flex items-center gap-x-1.5 sm:gap-x-2 h-16 sm:h-20 lg:h-[88px]">
              {/* Mobile hamburger — left column */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden justify-self-start relative z-10 w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center -ml-1"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`absolute block w-5 sm:w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-1.5 sm:-translate-y-2'
                  }`}
                />
                <span
                  className={`absolute block w-5 sm:w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`absolute block w-5 sm:w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-1.5 sm:translate-y-2'
                  }`}
                />
              </button>

              {/* Logo — true center column on mobile; left-aligned on desktop */}
              <EditableSlotGroup
                groupPath="header.logo"
                definition={logoSlotDef}
                className="justify-self-center lg:justify-self-auto flex-shrink-0"
              >
                <a href="/" className="flex-shrink-0 block">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-9 sm:h-11 lg:h-16 w-auto object-contain"
                      sizes="240px"
                      priority
                    />
                  ) : (
                    <div className="font-heading text-base sm:text-lg lg:text-2xl font-normal text-[var(--vibe-color-primary)]">
                      The Rise
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links (display:none on mobile — skipped by grid) */}
              <DesktopNav navLinks={navLinks} currentPath={currentPath} />

              {/* Schedule Tour CTA — right column; one line, compact on mobile */}
              <div className="justify-self-end min-w-0 flex justify-end items-center lg:ml-auto flex-shrink-0">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className="nav-cta-btn group/cta relative inline-flex items-center justify-center px-2 py-1.5 sm:px-2.5 sm:py-1.5 lg:px-6 lg:py-3 font-body text-[10px] sm:text-[11px] lg:text-base leading-none font-normal normal-case tracking-tight lg:tracking-normal whitespace-nowrap overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </span>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile Menu Overlay                                            */}
        {/* ============================================================== */}
        <div
          className={`mobile-menu-panel lg:hidden fixed inset-x-0 bottom-0 overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `${headerHeight}px`, backgroundColor: 'var(--vibe-color-background)' }}
        >
          {/* Navigation links */}
          <nav role="navigation" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <React.Fragment key={link.href || link.label}>
                <MobileNavLink link={link} onNavigate={closeMobileMenu} currentPath={currentPath} />
                {link.children?.map((child) => (
                  <MobileNavLink
                    key={child.href || child.label}
                    link={child}
                    onNavigate={closeMobileMenu}
                    currentPath={currentPath}
                    isChild
                  />
                ))}
              </React.Fragment>
            ))}
          </nav>

          {/* View Floor Plans CTA — distinct from the top-bar Schedule-a-Tour
              CTA so the drawer surfaces a different primary action. Uses the
              same `mobile-util-pill` outline-fill-on-hover treatment as the
              Apply Now / Resident Access pills below so all three drawer
              CTAs share a single visual language (primary-color border +
              label, primary-color fill on hover with text-on-primary label).
              Wrapped in EditableSlotGroup so the editor can target it from
              inside the open drawer (url / label / target). */}
          <div className="p-5">
            <EditableSlotGroup groupPath="header.view_floor_plans_link" definition={viewFloorPlansLinkDef}>
              <a
                href={viewFloorPlansLink.url || '/floor-plans'}
                target={viewFloorPlansLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill block text-center w-full rounded-full py-3.5 font-body text-sm font-medium tracking-[0.15em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                {viewFloorPlansLink.label || 'View Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Utility CTA Buttons — wrapped in EditableSlotGroup so the
              editor can target them from inside the open mobile drawer
              the same way it can in the desktop nav above. The wrappers
              are display:contents-ish in practice (they don't introduce
              visible chrome), so the two-column grid layout below still
              treats each anchor as a direct grid item. */}
          <div className="px-5 pb-3">
            <div className="grid grid-cols-2 gap-2">
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  className="mobile-util-pill block rounded-full text-center py-2.5 font-body text-[11px] font-medium tracking-wider border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  className="mobile-util-pill block rounded-full text-center py-2.5 font-body text-[11px] font-medium tracking-wider border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Address & Phone */}
          <div className="px-5 pb-6 pt-3 border-t border-[var(--vibe-color-border)]/50">
            <div className="space-y-3">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
                >
                  <MapPinIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                  <span>{addressLink.address}</span>
                </a>
              </EditableSlotGroup>
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
              >
                <PhoneIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                {/* Mirror the desktop phone treatment: EditableToken
                    wraps just the inner text span so the editor can
                    target the phone number from inside the open mobile
                    drawer. The outer <a> still routes to `tel:` for
                    real visitors. */}
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Flow spacer. The header is fixed + opaque, so it would otherwise sit
          on top of the page content. This reserves its height in the document
          flow so the hero (and every page below it) starts beneath the bar.
          Height tracks the measured header height once JS runs; the CSS
          fallback in the <style> block (offer-banner var + nav/util row
          heights) keeps the gap correct on first paint / SSG before the
          measurement, minimizing layout shift. */}
      <div
        className="header-spacer"
        aria-hidden="true"
        style={headerHeight ? { height: `${headerHeight}px` } : undefined}
      />

      <style>{`
        /* Spacer fallback height before JS measures the real header. Mobile
           shows only the nav row (h-20 = 5rem); the desktop (lg) layout adds
           the utility row (h-9 = 2.25rem) above the taller nav row (88px =
           5.5rem). The offer-banner var is added on top because the banner is
           fixed and the header reserves its height via header-bar padding. */
        .header-spacer {
          height: calc(var(--offer-banner-height, 0px) + 5rem);
        }
        @media (min-width: 1024px) {
          .header-spacer {
            height: calc(var(--offer-banner-height, 0px) + 7.75rem);
          }
        }

        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          /* Match the background-color transition curve so border + shadow
             fade in/out in lock-step with the transparent → solid swap. */
          border-bottom: 1px solid transparent;
          transition:
            border-color 0.5s ease,
            box-shadow 0.5s ease;
        }
        .header-scrolled .header-bar {
          border-bottom-color: var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* Transparent header: nav links → white (header bar only — NOT inside the mobile menu panel) */
        .header-transparent .header-bar a.font-body,
        .header-transparent .header-bar nav a,
        .header-transparent .header-bar button.font-body {
          color: rgba(255,255,255,0.92) !important;
        }
        .header-transparent .header-bar nav a:hover,
        .header-transparent .header-bar button.font-body:hover {
          color: #fff !important;
        }

        /* Interior pages (transparent nav over HeroSolidBG / primary color):
           swap white for the luminance-picked on-color token (textOnPrimary
           vs textOnSecondary, whichever has higher WCAG contrast against the
           primary background). The variable is set inline on the header. */
        .header-transparent.header-over-solid-hero .header-bar a.font-body,
        .header-transparent.header-over-solid-hero .header-bar nav a,
        .header-transparent.header-over-solid-hero .header-bar button.font-body {
          color: var(--rise-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent.header-over-solid-hero .header-bar nav a:hover,
        .header-transparent.header-over-solid-hero .header-bar button.font-body:hover {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* "More" dropdown panel sits on a theme-background (white) surface,
           so its links use theme text color regardless of the header bar's
           transparent state above. The dropdown is a white-background
           context, so the hover affordance is the animated underline from
           .rise-nav-darken-hover below — we keep the text color steady
           on hover (still !important to override the parent
           .header-transparent .header-bar nav a:hover white rule).

           The .header-over-solid-hero variants must be listed explicitly:
           .header-transparent.header-over-solid-hero .header-bar nav a has
           higher specificity (0,4,1) than the plain .header-transparent
           .header-bar .nav-more-dropdown a override (0,3,1) and would
           otherwise force the dropdown links to the picked on-hero color
           (white) — producing white-on-white text inside the dropdown on
           interior pages like Floor Plans. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* Dropdown sublinks (Features, Gallery & Tours, Find Your Home, More).
           The top-level double-bar hover is built for inline labels; in a
           stacked panel those bars sit on shared row edges and a previously
           hovered sibling can look "stuck" while the opacity fades. Use a
           left accent + tinted row instead so only the current sublink
           highlights, and the highlight clears immediately on leave. */
        .header-bar .nav-more-dropdown .nav-dropdown-link {
          background-color: transparent;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        .header-bar .nav-more-dropdown .nav-dropdown-link::after {
          content: none;
        }
        .header-bar .nav-more-dropdown .nav-dropdown-link::before {
          top: 0;
          bottom: 0;
          left: 0;
          right: auto;
          width: 3px;
          height: auto;
          background-color: var(--vibe-color-primary);
          opacity: 0;
          transition: opacity 0.15s ease;
        }
        @media (hover: hover) {
          .header-bar .nav-more-dropdown .nav-dropdown-link:hover {
            background-color: color-mix(in srgb, var(--vibe-color-primary) 8%, transparent);
            color: var(--vibe-color-primary) !important;
          }
          .header-bar .nav-more-dropdown .nav-dropdown-link:hover::before {
            opacity: 1;
          }
        }
        .header-bar .nav-more-dropdown .nav-dropdown-link.nav-link-active {
          background-color: color-mix(in srgb, var(--vibe-color-primary) 8%, transparent);
          color: var(--vibe-color-primary) !important;
        }
        .header-bar .nav-more-dropdown .nav-dropdown-link.nav-link-active::before {
          opacity: 1;
        }

        /* Transparent header: utility links → white */
        .header-transparent .header-util-link {
          color: rgba(255,255,255,0.85);
        }
        .header-transparent .header-util-link:hover {
          color: #fff;
        }
        .header-transparent .header-util-divider {
          background: rgba(255,255,255,0.3);
        }

        /* Interior pages: utility links + divider use the picked on-color
           token instead of hardcoded white. We dim the base state via
           color-mix() to match the ~85% / 30% feel of the photographic
           variant above. */
        .header-transparent.header-over-solid-hero .header-util-link {
          color: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 85%, transparent);
        }
        .header-transparent.header-over-solid-hero .header-util-link:hover {
          color: var(--rise-nav-over-hero-text, #fff);
        }
        .header-transparent.header-over-solid-hero .header-util-divider {
          background: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 30%, transparent);
        }

        /* Mobile menu panel uses --vibe-color-background (white), so links
           must render in theme text color regardless of the header bar's
           transparent / scrolled state above it. The hover affordance for
           nav links is the animated underline from .rise-nav-darken-hover
           below, so we hold text color steady on hover here too. */
        .mobile-menu-panel a,
        .mobile-menu-panel button {
          color: var(--vibe-color-text);
        }
        .mobile-menu-panel a:hover {
          color: var(--vibe-color-text);
        }

        /* Non-clickable synthetic group labels (Features, Gallery & Tours,
           Find Your Home). Styled as section headings so they don't read as
           dead links next to the tap targets below — smaller, uppercase,
           muted, with a faint tinted strip that separates the group. */
        .mobile-menu-panel .mobile-nav-group-label {
          display: block;
          padding: 1.15rem 1.5rem 0.45rem;
          font-family: var(--vibe-font-body);
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          background-color: color-mix(in srgb, var(--vibe-color-primary) 6%, var(--vibe-color-background));
          border-bottom: 1px solid color-mix(in srgb, var(--vibe-color-border) 80%, transparent);
          cursor: default;
          user-select: none;
        }

        /* ---------------------------------------------------------------- */
        /* Mobile drawer nav rows — DISTINCT hover/select treatment.       */
        /* ----------------------------------------------------------------- */
        /* The drawer rows share the .rise-nav-darken-hover / .nav-link-active
           classes with the desktop nav (which paints a top+bottom "double
           bar"). In the vertical drawer that reads as noise, so we override
           both here — scoped under .mobile-menu-panel so DESKTOP IS
           UNTOUCHED — with a left accent bar that slides in, a faint
           primary-tinted row background, and a small text nudge to the
           right. The active (selected) page keeps the bar + tint and shifts
           its label to the primary color. Higher specificity than the
           unscoped rules (0,2,x vs 0,1,x) so these win.

           We replace the inherited top/bottom bars with a single LEFT
           accent; padding-left is pinned to the row's base 1.5rem
           (Tailwind px-6) so the hover nudge animates smoothly. */
        .mobile-menu-panel .rise-nav-darken-hover {
          padding-left: 1.5rem;
          background-color: transparent;
          transition: background-color 0.2s ease, padding-left 0.2s ease, color 0.2s ease;
        }
        /* Drawer uses a left accent instead of the desktop top/bottom bars. */
        .mobile-menu-panel .rise-nav-darken-hover::before {
          top: 0;
          bottom: 0;
          left: 0;
          right: auto;
          width: 3px;
          height: auto;
          background-color: var(--vibe-color-primary);
        }
        .mobile-menu-panel .rise-nav-darken-hover::after {
          content: none;
        }
        @media (hover: hover) {
          .mobile-menu-panel .rise-nav-darken-hover:hover {
            padding-left: 1.75rem;
            background-color: color-mix(in srgb, var(--vibe-color-primary) 8%, transparent);
            color: var(--vibe-color-primary);
          }
        }
        .mobile-menu-panel .nav-link-active {
          padding-left: 1.75rem;
          background-color: color-mix(in srgb, var(--vibe-color-primary) 8%, transparent);
          color: var(--vibe-color-primary);
        }
        .mobile-menu-panel .nav-link-active::before {
          opacity: 1;
        }

        /* Child pages nest under their group heading. Padding is owned
           here (not Tailwind pl-*) because the drawer hover/active rules
           above pin padding-left and would otherwise fight an indent
           utility. The left accent bar stays on the panel edge. */
        .mobile-menu-panel .rise-nav-darken-hover.mobile-nav-child {
          padding-left: 2.25rem;
        }
        @media (hover: hover) {
          .mobile-menu-panel .rise-nav-darken-hover.mobile-nav-child:hover {
            padding-left: 2.5rem;
          }
        }
        .mobile-menu-panel .nav-link-active.mobile-nav-child {
          padding-left: 2.5rem;
        }

        /* Nav links: the hover affordance is the SAME "double bar" (top +
           bottom) used for the active page. Bars are ::before / ::after
           rules faded via opacity — NOT inset box-shadows. Transitioning
           box-shadow to/from transparent does not reliably reverse in
           Chromium/WebKit, so a previously hovered dropdown row kept its
           bars after the pointer moved to a sibling. Opacity interpolates
           cleanly, so only the current hover/active target stays lit.
           Used by the Header desktop links and the "More" dropdown items;
           the mobile drawer overrides ::before into a left accent above. */
        .rise-nav-darken-hover {
          position: relative;
        }
        .rise-nav-darken-hover::before,
        .rise-nav-darken-hover::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background-color: var(--vibe-color-text);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s ease;
        }
        .rise-nav-darken-hover::before { top: 0; }
        .rise-nav-darken-hover::after { bottom: 0; }
        @media (hover: hover) {
          .rise-nav-darken-hover:hover::before,
          .rise-nav-darken-hover:hover::after {
            opacity: 1;
          }
        }

        /* Active-page indicator — same top + bottom bars as hover, held
           at full opacity so the current page stays marked after the
           pointer leaves. */
        .nav-link-active::before,
        .nav-link-active::after {
          opacity: 1;
        }

        /* Dot separator between desktop nav page names. The margin-right
           matches the (now smaller) nav flex gap (gap-2 → gap-3 at xl) so the
           gap before the dot and the margin after it are equal — the dot sits
           centered between the two links, and the total link-to-link spacing
           is back to roughly what it was before separators were added (rather
           than the doubled spacing that stacked a full gap on each side).
           Full text color + heavier weight + a slightly larger glyph make the
           dot crisp and clearly defined instead of a faint speck. */
        .nav-sep {
          color: var(--vibe-color-text);
          font-weight: 400;
          font-size: 1.15em;
          margin-right: 0.5rem;
          line-height: 1;
          user-select: none;
        }
        @media (min-width: 1280px) {
          .nav-sep { margin-right: 0.75rem; }
        }

        /* Outline pills inside the drawer flip to text-on-primary on hover. */
        .mobile-menu-panel .mobile-util-pill:hover {
          color: var(--vibe-color-text-on-primary);
        }

        /* Transparent header: hamburger bars → white */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: #fff !important;
        }

        /* Transparent header: site name fallback text */
        .header-transparent .font-heading {
          color: #fff !important;
        }

        /* Interior pages: hamburger bars + site-name fallback also flip to
           the luminance-picked on-color token. */
        .header-transparent.header-over-solid-hero span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--rise-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero .font-heading {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* ================================================================ */
        /* Utility row — sits above the main nav row inside .header-bar.   */
        /* ----------------------------------------------------------------- */
        /* Subtle divider line between the utility row and the main nav row
           when the header is in its scrolled (white-background) state. The
           transparent/over-hero variants below soften the divider to a
           translucent tint of the picked on-color so it still reads against
           dark hero photography or the solid primary section without
           painting a hard line. */
        .header-util-row {
          border-bottom: 1px solid transparent;
          transition: border-color 0.5s ease;
        }
        .header-scrolled .header-util-row {
          border-bottom-color: var(--vibe-color-border);
        }
        .header-transparent .header-util-row {
          border-bottom-color: rgba(255, 255, 255, 0.18);
        }
        .header-transparent.header-over-solid-hero .header-util-row {
          border-bottom-color: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 18%, transparent);
        }

        /* ================================================================ */
        /* Inline utility links (phone, apply, resident)                   */
        /* ================================================================ */
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 400;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          /* Double-bar hover — same ::before / ::after bars as the main
             nav links so utility-row hover clears when the pointer moves
             to a sibling (box-shadow transitions were leaving the previous
             link highlighted). */
          position: relative;
          white-space: nowrap;
          transition: color 0.2s ease;
        }
        .header-util-link::before,
        .header-util-link::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background-color: var(--vibe-color-text);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s ease;
        }
        .header-util-link::before { top: 0; }
        .header-util-link::after { bottom: 0; }
        /* White-background hover matches the nav links' darken-25% rule.
           The .header-transparent and .header-over-solid-hero overrides
           below carry higher specificity so they still win when the
           header sits on top of a hero. */
        @media (hover: hover) {
          .header-util-link:hover {
            color: color-mix(in srgb, var(--vibe-color-primary), black 25%);
          }
          .header-util-link:hover::before,
          .header-util-link:hover::after {
            opacity: 1;
          }
        }
        .header-util-divider {
          display: block;
          width: 1px;
          height: 14px;
          background: var(--vibe-color-border);
          flex-shrink: 0;
        }
        /* Address sits next to the phone in the same utility row. Keep
           sentence case (street names aren't labels) and allow the text
           to ellipsize on mid-width desktops instead of colliding with
           Apply Now / Resident Access on the right. */
        .header-util-address {
          text-transform: none;
          letter-spacing: 0.02em;
          min-width: 0;
          flex: 0 1 auto;
          overflow: hidden;
        }
        .header-util-address span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }

        /* ================================================================ */
        /* CTA Button — solid filled style                                 */
        /* ----------------------------------------------------------------- */
        /* The Belmont demo renders the primary header CTA ("Schedule a Tour")
           as a SOLID filled button in the dark secondary brand color with
           white-on-secondary label — not the earlier transparent outline.
           A black ::before overlay fades in on hover for a subtle darken,
           and the fill stays solid in every header state (scrolled, over a
           photographic hero, and over a HeroSolidBG interior page) so the
           button reads as the same chip the demo shows regardless of what's
           behind the header. A faint translucent ring keeps it crisp when it
           sits over a dark hero photo. */
        .nav-cta-btn {
          background: var(--vibe-color-secondary);
          border: 1.5px solid var(--vibe-color-secondary);
          border-radius: 9999px;
          color: var(--vibe-color-text-on-secondary);
          box-shadow: none;
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: #000;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .nav-cta-btn:hover {
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent);
          transform: translateY(-1px);
          color: var(--vibe-color-text-on-secondary);
        }
        .nav-cta-btn:hover::before {
          opacity: 0.14;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* Over a transparent header (photographic or solid hero): keep the
           solid secondary fill, but soften the border to a translucent white
           ring so the chip separates from the imagery behind it. */
        .header-transparent .nav-cta-btn {
          border-color: rgba(255,255,255,0.35);
          color: var(--vibe-color-text-on-secondary);
        }
        .header-transparent .nav-cta-btn:hover {
          border-color: rgba(255,255,255,0.55);
          color: var(--vibe-color-text-on-secondary);
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule a Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.view_floor_plans_link': {
      label: 'View Floor Plans Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
